import type { FieldDefinition, FieldLevel, Meta } from '@numistic/contracts'
import { fieldsForType } from '@numistic/domain'
import { useTranslation } from '@numistic/i18n'
import { Button, Select } from '@numistic/ui'
import { useId, useMemo, useState } from 'react'

import { widgets } from './widgets/index.js'

/** Поля, которые можно задать сразу многим записям (кроме связей и списков). */
const BULK_TYPES = new Set([
  'text',
  'longtext',
  'integer',
  'decimal',
  'boolean',
  'date',
  'partial_date',
  'year_range',
  'enum',
  'multi_enum',
  'ref',
  'money',
  'grade',
  'url',
])

/**
 * «Одно поле — всем выбранным» (docs/PLAN.md, этап 3): выбор поля, значение виджетом из реестра.
 * Пустое значение очищает поле.
 */
export function BulkFieldEditor({
  meta,
  level,
  exclude = [],
  pending,
  onApply,
}: {
  meta: Meta
  level: FieldLevel
  exclude?: string[]
  pending?: boolean
  onApply: (values: Record<string, unknown>) => void
}) {
  const { t } = useTranslation()
  const id = useId()
  const fields = useMemo(() => {
    const codes = new Set(
      meta.itemTypes
        .filter((x) => x.active !== false)
        .flatMap((x) => fieldsForType(meta, x.code, level).map((f) => f.def.code)),
    )
    return meta.fields.filter(
      (f) => codes.has(f.code) && BULK_TYPES.has(f.dataType) && !exclude.includes(f.code),
    )
  }, [meta, level, exclude])
  const [code, setCode] = useState('')
  const [value, setValue] = useState<unknown>(null)
  const def: FieldDefinition | undefined = fields.find((f) => f.code === code)
  const Widget = def ? widgets[def.dataType] : undefined

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select
        className="w-56"
        aria-label={t('bulk.field')}
        value={code}
        onChange={(e) => {
          setCode(e.target.value)
          setValue(null)
        }}
      >
        <option value="">{t('bulk.field')}</option>
        {fields.map((f) => (
          <option key={f.code} value={f.code}>
            {f.name.ru}
          </option>
        ))}
      </Select>
      {def && Widget ? (
        <div className="min-w-48" aria-label={t('bulk.value')} role="group">
          <Widget
            key={def.code}
            id={id}
            meta={meta}
            field={{ def, required: false, section: def.ui.section, sortKey: '', override: false }}
            value={value}
            onChange={setValue}
          />
        </div>
      ) : null}
      {def ? (
        <Button
          size="sm"
          disabled={pending}
          onClick={() => onApply({ [def.code]: value === '' ? null : value })}
        >
          {t('bulk.apply')}
        </Button>
      ) : null}
    </div>
  )
}
